import { useSearchParams } from '@remix-run/react'
import { useCallback } from 'react'
import { resolveChatControls } from '#app/utils/chat-controls.ts'
import { trackUIEvent } from '#app/utils/plausible.client.js'

/**
 * Sidans regionval, som bor i URL-parametern ?region=. ETT val för hela sidan:
 * chattrutan, Snabbsök-kortet och navbarens sökdialog läser och skriver samma
 * parameter, så en region vald i dialogen gäller nästa fråga i chatten och
 * tvärtom. `location` säger var bytet gjordes.
 */
export function useRegionParam(location: string) {
	const [searchParams, setSearchParams] = useSearchParams()
	const { region } = resolveChatControls(searchParams)

	// Ett regionbyte startar INTE ny konversation: regionen är en egenskap hos
	// nästa fråga, inte hos tråden, och ChatPage läser den per förfrågan.
	const selectRegion = useCallback(
		(next: string) => {
			if (next === region) return
			// Ett val för hela sidan → ett event. from/to som förut.
			trackUIEvent({
				eventName: 'button_click',
				label: 'region_changed',
				location,
				extraProps: { from: region, to: next },
			})
			setSearchParams(
				prev => {
					const p = new URLSearchParams(prev)
					if (next === 'all') p.delete('region')
					else p.set('region', next)
					// AutoSubmitQuery strips ?q= with history.replaceState, which the
					// router never sees — so without this, writing the params back
					// would resurrect the already-answered query in the URL bar.
					p.delete('q')
					return p
				},
				{ replace: true },
			)
		},
		[region, location, setSearchParams],
	)

	return { region, selectRegion }
}
